import React, { useState } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Space,
  Typography,
  Divider,
  DatePicker,
} from "antd";
import { GiftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { device } from "../../lib/breakpoints";
import { invoiceApi } from "../../lib/api/paymentApi";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * InvoiceAdjustmentModal - Modal for adding tenant ledger credits/adjustments
 *
 * This is a CREDIT MEMO - not a cash payment. Visually distinct from "Record Payment".
 * Allows negative amounts for credits (e.g., -$50 for repair work done by tenant).
 */
const InvoiceAdjustmentModal = ({
  visible,
  onCancel,
  onSuccess,
  invoice,
  invoiceId,
  tenantName = "this tenant",
  tenants = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Resolve tenant name from invoice if not passed
  const resolvedTenantName =
    tenantName ||
    (invoice?.tenantId &&
      tenants.find((t) => t.id === invoice.tenantId)?.name) ||
    "this tenant";

  // Calculate current totals
  const baseAmount = invoice?.amount || 0;
  const existingAdjustments = invoice?.adjustments || [];
  const adjustmentTotal = existingAdjustments.reduce(
    (sum, adj) => sum + (adj.amount || 0),
    0
  );
  const currentTotal = baseAmount + adjustmentTotal;

  const performSubmit = async (values) => {
    const id = invoiceId || invoice?.id;
    if (!id) {
      throw new Error("No invoice selected");
    }
    const payload = {
      description: values.description,
      amount: values.amount,
      date: values.date
        ? (
            values.date?.toDate?.() ||
            values.date?.$d ||
            new Date(values.date)
          )?.toISOString?.()
        : undefined,
    };
    return invoiceApi.addAdjustment(id, payload);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const amount = Number(values.amount) || 0;
      const isCredit = amount < 0;

      setLoading(true);
      try {
        const result = await performSubmit(values);
        message.success(
          isCredit
            ? "Credit applied successfully"
            : "Adjustment added successfully"
        );
        form.resetFields();
        onSuccess?.(result);
        onCancel();
      } catch (error) {
        const displayMessage =
          error?.data?.code === "NEGATIVE_TOTAL_NOT_ALLOWED"
            ? error.data?.message ||
              error.data?.error ||
              "Credits cannot exceed the amount due."
            : error?.message ||
              (isCredit ? "Failed to add credit" : "Failed to add adjustment");
        message.error(displayMessage);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      if (error.errorFields) {
        message.error("Please fix the form errors");
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const previewAmount = Form.useWatch("amount", form) || 0;
  const previewTotal = currentTotal + previewAmount;
  const descriptionValue = Form.useWatch("description", form) || "";
  const descriptionLength = descriptionValue.length;

  return (
    <StyledModal
      title={
        <ModalHeader>
          <GiftOutlined
            style={{
              color: "white",
              marginRight: "8px",
              filter: "brightness(0) invert(1)",
            }}
          />
          Add Credit / Adjustment
        </ModalHeader>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Add Adjustment"
      cancelText="Cancel"
      okButtonProps={{
        style: {
          background: "var(--color-primary)",
          borderColor: "var(--color-primary)",
        },
      }}
      width={600}
      centered
      destroyOnClose
    >
      <CreditMemoNotice>
        Reduces balance due. Not a cash payment.
      </CreditMemoNotice>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          amount: undefined,
          description: "",
          date: dayjs(),
        }}
      >
        {/* Current Invoice Summary */}
        <SummaryCard>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <SummaryRow>
              <SummaryLabel>Tenant:</SummaryLabel>
              <SummaryValue>{resolvedTenantName}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Base Amount:</SummaryLabel>
              <SummaryValue>
                $
                {baseAmount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </SummaryValue>
            </SummaryRow>
            {existingAdjustments.length > 0 && (
              <>
                <Divider style={{ margin: "8px 0" }} />
                <SummaryRow>
                  <SummaryLabel>Existing Adjustments:</SummaryLabel>
                  <SummaryValue>
                    {adjustmentTotal >= 0 ? "+" : ""}$
                    {adjustmentTotal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel $strong>Current Total:</SummaryLabel>
                  <SummaryValue $strong>
                    $
                    {currentTotal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </SummaryValue>
                </SummaryRow>
              </>
            )}
            {existingAdjustments.length === 0 && (
              <SummaryRow>
                <SummaryLabel $strong>Current Total:</SummaryLabel>
                <SummaryValue $strong>
                  $
                  {currentTotal.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </SummaryValue>
              </SummaryRow>
            )}
          </Space>
        </SummaryCard>

        <Form.Item
          label="Date"
          name="date"
          rules={[{ required: true, message: "Pick a date" }]}
        >
          <DatePicker style={{ width: "100%" }} placeholder="Select date" />
        </Form.Item>

        <Form.Item
          label="Adjustment Amount"
          name="amount"
          rules={[
            {
              required: true,
              validator: (_, value) => {
                if (value === null || value === undefined || value === "") {
                  return Promise.reject(new Error("Enter an amount"));
                }
                if (
                  typeof value === "number" &&
                  (isNaN(value) || !isFinite(value))
                ) {
                  return Promise.reject(new Error("Enter a valid number"));
                }
                return Promise.resolve();
              },
            },
          ]}
          extra={
            <HelperText>
              Enter a negative amount for credits (e.g., -50.00) or positive for
              additional charges
            </HelperText>
          }
        >
          <StyledInputNumber
            prefix={<span style={{ color: "#1f2937" }}>$</span>}
            placeholder="0.00"
            min={-999999}
            max={999999}
            step={0.01}
            precision={2}
            formatter={(value) =>
              value != null && value !== "" && !Number.isNaN(Number(value))
                ? Number(value).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                  })
                : ""
            }
            parser={(displayValue) => {
              const s = String(displayValue ?? "").replace(/,/g, "");
              if (s === "" || s === "-") return undefined;
              const n = parseFloat(s);
              return Number.isNaN(n) ? undefined : n;
            }}
          />
        </Form.Item>

        <Form.Item
          label="Reason / Description"
          name="description"
          rules={[
            { required: true, message: "Enter a reason for this credit" },
            {
              max: 500,
              message: "Description must be less than 500 characters",
            },
          ]}
          extra={
            <>
              <HelperText>
                Explain the reason (e.g., &quot;Credit for repair work completed
                by tenant&quot;, &quot;Late fee waiver&quot;)
              </HelperText>
              <CharacterCount>{descriptionLength} / 500</CharacterCount>
            </>
          }
        >
          <StyledTextArea
            rows={3}
            placeholder="e.g., Credit for repair work completed by tenant"
            maxLength={500}
          />
        </Form.Item>

        {previewAmount !== 0 && (
          <PreviewCard $isNegative={previewTotal < 0}>
            <PreviewRow>
              <PreviewLabel>New Total After Adjustment:</PreviewLabel>
              <PreviewValue $isNegative={previewTotal < 0}>
                $
                {previewTotal.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </PreviewValue>
            </PreviewRow>
          </PreviewCard>
        )}
      </Form>
    </StyledModal>
  );
};

export default InvoiceAdjustmentModal;

const CreditMemoNotice = styled.div`
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  color: #0050b3;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 20px;

  @media ${device.mobile} {
    padding: 8px 12px;
    font-size: 0.85rem;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 16px;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;

    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: white !important;
      fill: white !important;
    }
  }

  .ant-modal-body {
    padding: 24px;
    background: #fff;
    overflow-x: hidden;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0;
  }

  .ant-modal {
    margin: 20px auto;
  }

  /* Form field spacing */
  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-form-item-label > label {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .ant-input-number,
  .ant-input {
    font-size: 0.95rem;
  }

  .ant-input-number {
    height: 40px;
  }

  /* Fix InputNumber prefix inner border - completely remove separator */
  .ant-input-number-prefix {
    border-right: none !important;
    padding-right: 8px !important;
    background: transparent !important;
    margin-right: 0 !important;
  }

  .ant-input-number-input-wrap {
    border-left: none !important;
    padding-left: 4px !important;
    margin-left: 0 !important;
  }

  /* Remove any internal borders within InputNumber */
  .ant-input-number-input {
    border: none !important;
  }

  /* Ensure the InputNumber container has a single clean border */
  .ant-input-number {
    border: 1px solid #d9d9d9 !important;

    &:hover:not(.ant-input-number-disabled) {
      border-color: var(--color-primary) !important;
    }

    &.ant-input-number-focused {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 2px rgba(86, 228, 157, 0.1) !important;
    }
  }

  /* Hide Ant Design's built-in character counter since we're using custom one */
  .ant-input-data-count {
    display: none !important;
  }

  /* Fix TextArea double border - ensure single border */
  .ant-input {
    border: 1px solid #d9d9d9 !important;
    outline: none !important;

    &:focus,
    &:hover {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 2px rgba(86, 228, 157, 0.1) !important;
      outline: none !important;
    }
  }

  /* Remove duplicate error messages - show only the first one */
  .ant-form-item-explain-error {
    margin-top: 4px;
  }

  .ant-form-item-explain-error:not(:first-of-type) {
    display: none !important;
  }

  .ant-btn {
    height: 40px;
    font-size: 0.95rem;
    font-weight: 500;
    border-radius: 8px;
  }

  .ant-btn-primary {
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;

    &:hover {
      background: #2d5a2d !important;
      border-color: #2d5a2d !important;
    }
  }

  @media ${device.mobile} {
    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      top: 12px;
      right: 16px;
      font-size: 14px;
    }

    .ant-form-item {
      margin-bottom: 16px;
    }
  }

  @media (max-width: 480px) {
    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }

    .ant-form-item {
      margin-bottom: 14px;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: white;

  @media ${device.mobile} {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const SummaryCard = styled.div`
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;

  @media ${device.mobile} {
    padding: 14px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 16px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryLabel = styled.span`
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: ${(props) => (props.$strong ? 600 : 400)};

  @media ${device.mobile} {
    font-size: 0.85rem;
  }
`;

const SummaryValue = styled.span`
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: ${(props) => (props.$strong ? 600 : 400)};

  @media ${device.mobile} {
    font-size: 0.85rem;
  }
`;

const HelperText = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-light);
  display: block;
  margin-top: 4px;
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.75rem;
  }
`;

const PreviewCard = styled.div`
  background: ${(props) => (props.$isNegative ? "#fff1f0" : "#e6f7ff")};
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;
  border: 1px solid ${(props) => (props.$isNegative ? "#ffccc7" : "#91d5ff")};

  @media ${device.mobile} {
    padding: 10px 14px;
    margin-top: 14px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    margin-top: 12px;
  }
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PreviewLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text);

  @media ${device.mobile} {
    font-size: 0.85rem;
  }
`;

const PreviewValue = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${(props) => (props.$isNegative ? "#ff4d4f" : "#1890ff")};

  @media ${device.mobile} {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;

  &.ant-input-number {
    display: flex !important;
    align-items: center !important;
    border: 1px solid #d9d9d9 !important;
  }

  .ant-input-number-input-wrap {
    display: flex !important;
    align-items: center !important;
    flex: 1 !important;
    min-height: 0 !important;
    border-left: none !important;
    padding-left: 4px !important;
    height: 100% !important;
  }

  /* Vertically center the amount text: match line-height to height and remove vertical padding */
  .ant-input-number-input {
    border: none !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 38px !important;
    height: 38px !important;
    box-sizing: border-box !important;
  }

  /* Remove inner border between prefix and input */
  .ant-input-number-prefix {
    border-right: none !important;
    margin-right: 0 !important;
    padding-right: 8px !important;
    background: transparent !important;
    display: flex !important;
    align-items: center !important;
  }

  /* Remove any inner borders or separators */
  .ant-input-number-handler-wrap {
    border-left: none !important;
  }

  &.ant-input-number:hover {
    border-color: var(--color-primary) !important;
  }

  &.ant-input-number-focused,
  &.ant-input-number:focus-within {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(86, 228, 157, 0.1) !important;
  }
`;

const StyledTextArea = styled(TextArea)`
  border: 1px solid #d9d9d9 !important;
  border-radius: 8px !important;

  &:focus,
  &:hover {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(86, 228, 157, 0.1) !important;
  }

  /* Remove any inner borders or double borders */
  &::before,
  &::after {
    display: none !important;
  }

  /* Ensure single border */
  border-width: 1px !important;
`;

const CharacterCount = styled.div`
  text-align: right;
  margin-top: 6px;
  font-size: 0.75rem;
  color: var(--color-text-light);
  line-height: 1.2;
`;
